import { useRef, useState } from "react";
import { RemiButton, RemiOTP, useEffect } from "@remi/next-ui";

const RESEND_COOLDOWN_SECONDS = 15;

type MfaChallengeSmsProps = {
  maskedIdentifier: string;
  onVerify: (code: string) => void;
  onResend: () => void;
  onTryAnotherMethod?: () => void;
  resetError?: () => void;
  loading: boolean;
  error: boolean;
  errorMessage?: string;
};

export const MfaChallengeSms = ({
  maskedIdentifier,
  onVerify,
  onResend,
  onTryAnotherMethod,
  resetError,
  loading,
  error,
  errorMessage,
}: MfaChallengeSmsProps) => {
  const [code, setCode] = useState("");
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN_SECONDS);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    if (countdown <= 0) {
      return undefined;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleChange = (next: string) => {
    // Clear the error on first new keystroke so RemiOTP's auto-reset
    // (which fires on slot focus when error===true) doesn't swallow input.
    if (error) {
      resetError?.();
    }
    setCode(next);
    if (next.length === 6 && !loading) {
      onVerify(next);
    }
  };

  const handleResend = () => {
    setCountdown(RESEND_COOLDOWN_SECONDS);
    setCode("");
    onResend();
  };

  return (
    <div className="flex flex-col gap-6" data-testid="mfa-challenge-sms">
      <div className="flex flex-col gap-1">
        <h2 ref={headingRef} tabIndex={-1} className="font-semibold text-lg text-text-primary">
          Check your phone
        </h2>
        <p className="text-sm text-text-secondary">Enter the 6-digit code sent to {maskedIdentifier}</p>
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <RemiOTP
            value={code}
            onChange={handleChange}
            error={error}
            resetValue={() => setCode("")}
            hideSeparator
            disabled={loading}
            slotClassName="h-14 flex-1 first:rounded-l-lg last:rounded-r-lg"
            groupClassName="w-full"
            containerClassName="w-full"
            autoComplete="one-time-code"
            inputMode="numeric"
            aria-label="6-digit verification code"
            data-testid="mfa-otp-input"
          />
          {error && errorMessage && (
            <span role="alert" className="text-[10px] text-text-destructive" data-testid="mfa-otp-input-error-message">
              {errorMessage}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-text-secondary text-xs">
          <span>Didn&apos;t receive a code?</span>
          <RemiButton
            variant="tertiary"
            size="sm"
            onClick={handleResend}
            disabled={countdown > 0 || loading}
            className="px-1 text-text-accent disabled:text-text-tertiary"
            data-testid="mfa-resend-sms"
          >
            Resend{countdown > 0 ? ` (${countdown}s)` : ""}
          </RemiButton>
        </div>
        {onTryAnotherMethod && (
          <RemiButton
            variant="tertiary"
            size="sm"
            onClick={onTryAnotherMethod}
            className="-ml-2 self-start text-text-secondary"
            data-testid="mfa-try-another-method"
          >
            Try another method
          </RemiButton>
        )}
      </div>
    </div>
  );
};
